import {
  CpsLocale,
  USER_LOCALE_SETTINGS_KEY,
  CPS_LOCALE,
  DEFAULT_LOCALE,
  IdNamePair,
  LOCALE_MAPPING
} from '@cs-cms/types';
import { IntlProvider, loadMessages, LocalizationProvider } from '@progress/kendo-react-intl';
import React, { ReactNode, useState } from 'react';
import { GlobalizationContext } from '../contexts/GlobalizationContext';
import ServicesContext from '../contexts/ServicesContext';
import { useGetTranslations } from '../hooks/translations/useGetTranslations';

interface GlobalizationProviderProps {
  children: ReactNode;
}

export interface GearLanguage {
  locale: CpsLocale;
  text: string;
  icon?: string;
}

const getLanguageFromLocale = (locale: CpsLocale) => {
  return locale.split(/[_-]/)[0];
};

const setLocaleSettingsKey = (locale: string) => {
  window.localStorage.setItem(USER_LOCALE_SETTINGS_KEY, locale);
};

const getLocaleSettingsKey = () => {
  return window.localStorage.getItem(USER_LOCALE_SETTINGS_KEY);
};

const getSupportedLocale = (localLocale: string) => {
  if (Object.values(CPS_LOCALE).includes(localLocale as CpsLocale)) {
    return localLocale as CpsLocale;
  } else {
    return DEFAULT_LOCALE;
  }
};

const getUserLocale = () => {
  const storedLocale = getLocaleSettingsKey();
  if (!storedLocale) {
    const browserLocale = navigator.language;
    return getSupportedLocale(browserLocale);
  }
  return getSupportedLocale(storedLocale);
};

export const GlobalizationProvider: React.FC<GlobalizationProviderProps> = props => {
  const [selectedLocale, setSelectedLocale] = useState<CpsLocale>(DEFAULT_LOCALE);
  const [availableLocales, setAvailableLocales] = useState<IdNamePair[] | undefined>(undefined);
  const servicesCtx = React.useContext(ServicesContext);

  const { data: translations } = useGetTranslations(selectedLocale);

  React.useEffect(() => {
    if (selectedLocale && translations) {
      const kendoLocale = LOCALE_MAPPING[selectedLocale as keyof typeof LOCALE_MAPPING];
      if (kendoLocale) {
        loadMessages(translations, kendoLocale);
      }
    }
  }, [selectedLocale, translations]);

  React.useEffect(() => {
    const fetchLocales = async () => {
      try {
        if (servicesCtx?.localesService) {
          const locales = await servicesCtx.localesService.getLocales();
          setAvailableLocales(locales);
        }
      } catch {
        // empty
      }
    };

    const userLocale = getUserLocale();
    setSelectedLocale(userLocale);
    fetchLocales();
  }, [servicesCtx]);

  const handleSetLocale = (newLocale: CpsLocale) => {
    const userLocale = getSupportedLocale(newLocale);
    setLocaleSettingsKey(userLocale);
    setSelectedLocale(userLocale);
  };

  if (!availableLocales) {
    return null;
  }

  return (
    <GlobalizationContext.Provider
      value={{
        availableLocales,
        selectedLocale,
        setSelectedLocale: handleSetLocale
      }}
    >
      <IntlProvider locale={selectedLocale}>
        <LocalizationProvider language={getLanguageFromLocale(selectedLocale)}>{props.children}</LocalizationProvider>
      </IntlProvider>
    </GlobalizationContext.Provider>
  );
};
